import React, { useCallback } from "react"
import { Box, Stack } from "@mui/material"
import RocketIcon from "src/assets/icons/rocket.svg"
import { BigNumber } from "ethers"
import { formatUnits } from "ethers/lib/utils"
import {
  FER,
  PoolName,
  POOLS_MAP,
  REWARD_TOKENS,
  LCRO_WCRO_POOL_NAME,
} from "src/constants"
import { useClaimReward } from "src/hooks/useClaimReward"
import { useIsMobile } from "src/hooks/useIsMobile"
import { getChainId } from "src/utils"

import Button from "src/components/Button"
import Text from "src/components/Text"
import usePoolData from "src/hooks/usePoolData"
import { useNavigate } from "react-router-dom"
import { formatNumber } from "src/utils/parseStringToBigNumber"
import { getAddress } from "src/utils/addressHelpers"
import { CroPoolWarning } from "src/components/CroWarning"
import { useGetBoostByPid } from "src/hooks/BoostFarming/useFarmBoostContract"
import { useClaimBoostRewards } from "src/hooks/BoostFarming/useFarmBoostHandler"

interface Props {
  amount: BigNumber
  poolName: PoolName
  openModal: () => void
  setHarvestAmount: React.Dispatch<React.SetStateAction<BigNumber>>
  setHarvest: React.Dispatch<React.SetStateAction<() => Promise<void>>>
  rewardsV2?: {
    tokens: string[]
    earnings: BigNumber[]
  }
}

const PoolRow = ({
  amount,
  poolName,
  setHarvestAmount,
  setHarvest,
  openModal,
  rewardsV2,
}: Props) => {
  const { claim } = useClaimReward(amount, poolName, rewardsV2)
  const isMobile = useIsMobile()
  const [, userShareData] = usePoolData(poolName)
  const navigate = useNavigate()

  const pool = POOLS_MAP[poolName]
  let enableHarvest = amount.gt(0)
  const chainId = getChainId()

  const { data: boostedRewards } = useGetBoostByPid(pool?.rewardPids[chainId])
  const { claim: claimBoost } = useClaimBoostRewards(
    boostedRewards || BigNumber.from(0),
    pool?.rewardPids[chainId],
  )

  const goToDeposit = useCallback(() => {
    navigate(`/pools/${pool.route}/deposit`)
  }, [navigate, pool.route])

  const goToStake = useCallback(() => {
    navigate(`/pools/${pool.route}/stake`)
  }, [navigate, pool.route])

  const onHarvestClick = async (isBoost = false) => {
    setHarvestAmount(isBoost ? boostedRewards || BigNumber.from(0) : amount)
    if (isBoost) {
      setHarvest(() => claimBoost)
    } else {
      setHarvest(() => claim)
    }
    if (rewardsV2) {
      await claim()
    } else {
      openModal()
    }
  }

  const renderReward = () => {
    return (
      <Text>
        {amount.isZero()
          ? "-"
          : `${FER.symbol} ${formatNumber(
              formatUnits(amount, FER.decimals),
              2,
              6,
            )}`}
      </Text>
    )
  }
  const renderRewardV2 = () => {
    enableHarvest = false
    return rewardsV2
      ? rewardsV2.tokens.map((tokenAddress, index) => {
          const rewardToken = REWARD_TOKENS.find(
            (token) => getAddress(token.addresses) === tokenAddress,
          )
          const earning = rewardsV2?.earnings[index]
          if (earning?.gt(0)) {
            enableHarvest = true
          }
          return (
            (earning?.gt(0) || index < rewardsV2?.tokens.length - 1) &&
            rewardToken && (
              <Text key={tokenAddress}>
                {earning.isZero()
                  ? "-"
                  : `${rewardToken.symbol} ${formatNumber(
                      formatUnits(earning, rewardToken.decimals),
                      2,
                      6,
                    )}`}
              </Text>
            )
          )
        })
      : "-"
  }

  return (
    <Box mt={3} pr={isMobile ? 0 : 2}>
      <Box
        display={isMobile ? "block" : "flex"}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" alignItems="center">
          <img src={pool.lpToken.icon} height={20} />
          <Text bold ml={1}>
            {poolName}
          </Text>
        </Stack>
        <Box display={isMobile ? "block" : "flex"} alignItems="center">
          <Box mr={isMobile ? 0 : 3} mt={isMobile ? 1 : 0}>
            {pool.version === 2 ? renderRewardV2() : renderReward()}
          </Box>
          <Box width={isMobile ? "100%" : 103}>
            {userShareData?.totalAmount.isZero() ? (
              <Button
                onClick={goToDeposit}
                size="small"
                mt={isMobile ? 1 : 0}
                kind="outline"
                fullWidth
              >
                Add
              </Button>
            ) : !userShareData || userShareData?.stakedAmount.isZero() ? (
              <Button
                onClick={goToStake}
                size="small"
                mt={isMobile ? 1 : 0}
                kind="outline"
                fullWidth
              >
                Stake
              </Button>
            ) : (
              <Button
                onClick={() => onHarvestClick(false)}
                size="small"
                mt={isMobile ? 1 : 0}
                disabled={!enableHarvest}
                fullWidth
              >
                Harvest
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      {boostedRewards?.gt(0) && (
        <Box
          display={isMobile ? "block" : "flex"}
          alignItems="center"
          justifyContent="flex-end"
          mt={1}
        >
          <Box
            mr={isMobile ? 0 : 3}
            mt={isMobile ? 1 : 0}
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            <Text bold size="small" color="#00A68C">
              Boosted
            </Text>
            <img src={RocketIcon} height={12} />
            <Text>
              {`${FER.symbol} ${formatNumber(
                formatUnits(boostedRewards, FER.decimals),
                2,
                6,
              )}`}
            </Text>
          </Box>
          <Box width={isMobile ? "100%" : 103}>
            <Button
              onClick={() => onHarvestClick(true)}
              size="small"
              mt={isMobile ? 1 : 0}
              fullWidth
            >
              Harvest
            </Button>
          </Box>
        </Box>
      )}
      {poolName === LCRO_WCRO_POOL_NAME && <CroPoolWarning />}
    </Box>
  )
}

export default PoolRow
